import { CATALOG_COLORS } from "@/app/utils/pageTheme";

// The design tokens the catalog's props choose from, and the theme colour each
// colour name resolves to. Each resolves to a CSS variable defined in
// main.css; colours, radius and fonts come from the page's theme. Their CSS
// (spacing, corners, shadows, patterns) is the renderers'
// (app/views/components/catalog/tokens.ts).

export const SPACE = ["none", "xs", "sm", "md", "lg", "xl", "2xl"] as const;
export type Space = (typeof SPACE)[number];

export const SECTION_SPACE = ["none", "sm", "md", "lg", "xl"] as const;
export type SectionSpace = (typeof SECTION_SPACE)[number];

export const RADIUS = ["none", "sm", "md", "lg", "xl", "full"] as const;
export type Radius = (typeof RADIUS)[number];

export const SHADOW = ["none", "sm", "md", "lg"] as const;
export type Shadow = (typeof SHADOW)[number];

// How much of a Cover's tint covers its picture, in percent: its "overlay".
export const OVERLAY_OPACITY = {
  none: 0,
  faint: 10,
  light: 25,
  medium: 45,
  strong: 65,
  heavy: 80,
  solid: 100,
} as const;
export type Overlay = keyof typeof OVERLAY_OPACITY;

export const PATTERN = ["none", "grid", "dots"] as const;
export type Pattern = (typeof PATTERN)[number];

// A colour prop holds a theme colour's name or, as an escape hatch, any CSS
// colour.
export function color(value: string | undefined) {
  if (!value) return undefined;
  return (CATALOG_COLORS as readonly string[]).includes(value) ? `hsl(var(--${value}))` : value;
}

// The text colour that reads on each theme colour used as a ground, as the
// theme pairs them: each base with its "-foreground", the pale grounds with
// the page's text.
const INKS: Record<(typeof CATALOG_COLORS)[number], (typeof CATALOG_COLORS)[number]> = {
  background: "foreground",
  foreground: "background",
  card: "foreground",
  muted: "foreground",
  "muted-foreground": "background",
  primary: "primary-foreground",
  "primary-foreground": "primary",
  secondary: "secondary-foreground",
  "secondary-foreground": "secondary",
  accent: "accent-foreground",
  "accent-foreground": "accent",
  border: "foreground",
  "primary-subtle": "foreground",
  "accent-subtle": "foreground",
};

// The text colour that reads on a background of `value`; none for a CSS
// colour, whose ink isn't known.
export function ink(value: string | undefined) {
  return value && Object.hasOwn(INKS, value) ? color(INKS[value as keyof typeof INKS]) : undefined;
}
